import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { DataScopeModel } from 'projects/core/src/lib/models/data-scope.model';
import { RoleModel } from 'projects/core/src/lib/models/role.model';

@Component({
  selector: 'app-role-data-scopes',
  templateUrl: './role-data-scopes.component.html',
  styleUrls: ['./role-data-scopes.component.scss']
})
export class RoleDataScopesComponent extends BaseForm implements OnInit {

  @Input() role: RoleModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public loading = false;
  public loadError = false;
  public scopes: Array<DataScopeModel> = [];

  public editRight = 'admin.role.edit';

  public form = this.fb.group({
    data_scopes: new UntypedFormControl([]),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private transloco: TranslocoService,
    private auth: AuthStateService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.api.getModels('data-scopes').subscribe({
      next: (result: any) => {
        this.scopes = [];
        if (result.data?.length > 0) {
          result.data.forEach((scope: any) => {
            scope = new DataScopeModel().deserialize(scope);
            scope.setTranslocoService(this.transloco);
            this.scopes.push(scope);
          });
        }
        this.refreshScopeOptionTitles();
        if (this.inEdit === true) {
          this.patchScopes();
        }
      },
      error: (error: any) => {
        this.loadError = true;
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onSubmit(): void {
    if (this.loadError === true) {
      return;
    }
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        data_scopes: this.getScopeIds()
      };
      this.api.saveRoleDataScopes(this.role.id, payload).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved'), this.transloco.translate('common.saved'));
          this.role.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  isFullAccess(): boolean {
    if (this.loading === true || this.role === null) {
      return false;
    }
    const assignedIds = this.getAssignedScopeIds();
    if (assignedIds.length === 0) {
      return true;
    }
    return !assignedIds.some((id: number) => this.isScopeActive(id));
  }

  getEffectiveRestrictionLabel(): string {
    const totals: { [dimension: string]: number } = {};
    const seen: Array<string> = [];
    this.getActiveAssignedScopes().forEach((scope: DataScopeModel) => {
      (scope.dimensions || []).forEach((dimension: string) => {
        if (seen.indexOf(dimension) === -1) {
          seen.push(dimension);
          totals[dimension] = 0;
        }
        const count = scope.counts ? scope.counts[dimension] : null;
        if (count !== undefined && count !== null && count !== '') {
          totals[dimension] += Number(count);
        }
      });
    });
    const preferred = ['template', 'businessunit'];
    seen.sort((a: string, b: string) => {
      const ia = preferred.indexOf(a);
      const ib = preferred.indexOf(b);
      return (ia === -1 ? preferred.length : ia) - (ib === -1 ? preferred.length : ib);
    });
    return seen.map((dimension: string) => {
      let label = dimension;
      if (dimension === 'template') {
        label = this.transloco.translate('common.labels.procedures');
      } else if (dimension === 'businessunit') {
        label = this.transloco.translate('common.labels.businessunits');
      }
      return label + ' (' + totals[dimension] + ')';
    }).join(', ');
  }

  getScopeNames(): string {
    if (this.isFullAccess() === true) {
      return this.transloco.translate('common.labels.full_data_access');
    }
    const assignedIds = this.getAssignedScopeIds();
    const names: Array<string> = [];
    this.scopes.forEach((scope: DataScopeModel) => {
      if (assignedIds.indexOf(scope.id) !== -1) {
        names.push(this.formatScopeTitle(scope));
      }
    });
    return names.join(', ');
  }

  protected startEdit(): void {
    if (this.loadError === true) {
      return;
    }
    if (this.auth.hasRight(this.editRight)) {
      this.patchScopes();
      this.setEdit.emit('data-scopes');
      super.startEdit();
      this.ui.setFormFocus();
    }
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
  }

  private patchScopes(): void {
    if (this.role !== null) {
      const selected = this.scopes.filter((scope: DataScopeModel) => {
        return (this.role.data_scopes || []).indexOf(scope.id) !== -1;
      });
      this.form.patchValue({
        data_scopes: selected
      });
    }
  }

  private getScopeIds(): Array<number> {
    const ids: Array<number> = [];
    const selected = this.form.get('data_scopes').value;
    if (Array.isArray(selected)) {
      selected.forEach((scope: DataScopeModel) => {
        ids.push(scope.id);
      });
    }
    return ids;
  }

  private getAssignedScopeIds(): Array<number> {
    if (this.inEdit === true) {
      const selected = this.form.get('data_scopes').value;
      if (!Array.isArray(selected)) {
        return [];
      }
      return selected.map((scope: DataScopeModel) => scope.id);
    }
    return this.role.data_scopes || [];
  }

  private getActiveAssignedScopes(): Array<DataScopeModel> {
    const assignedIds = this.getAssignedScopeIds();
    return this.scopes.filter((scope: DataScopeModel) => {
      return assignedIds.indexOf(scope.id) !== -1 && scope.active === true;
    });
  }

  private isScopeActive(id: number): boolean {
    const scope = this.scopes.find((item: DataScopeModel) => item.id === id);
    return scope !== undefined && scope.active === true;
  }

  private formatScopeTitle(scope: DataScopeModel): string {
    if (scope.active === true) {
      return scope.translated_title;
    }
    return scope.translated_title + ' (' + this.transloco.translate('common.labels.inactive') + ')';
  }

  private refreshScopeOptionTitles(): void {
    this.scopes.forEach((scope: DataScopeModel) => {
      (scope as DataScopeModel & { option_title?: string }).option_title = this.formatScopeTitle(scope);
    });
  }

}
